/**
 * Printable year-end tax summary + payment schedule for Link Ride.
 *
 * Renders standalone at /admin/taxes/print?year=YYYY — no nav, no sidebar,
 * just the report. User hits Cmd+P / Ctrl+P → "Save as PDF" → hand to CPA.
 *
 * Pulls everything from /api/admin/tax-report which already aggregates:
 *   - completed-booking gross + sales tax + driver payouts
 *   - subscription_payment + credit_pack_purchase revenue events
 *   - signup_bonus + admin_pro_comp + admin_credit_grant write-offs
 *   - mercantile preview
 */

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";

// Static payment-schedule template. Dates are MM-DD; we splice the year in
// at render time. PA Sales Tax frequency adapts based on the collected total.
function buildSchedule(year, salesTaxYearTotal) {
  const items = [];

  // PA Sales Tax — frequency depends on annual liability per PA DOR rules:
  //   < $100/quarter ($300/yr)          → annual filing (Jan 20)
  //   $100–$600/quarter ($1,200–$7,200) → quarterly
  //   $600+/quarter ($2,400+/yr)        → monthly
  const annualSalesTax = Number(salesTaxYearTotal) || 0;
  let stFreq = "Annual";
  let stDates = [`${year + 1}-01-20`];
  if (annualSalesTax >= 2400) {
    stFreq = "Monthly (20th of following month)";
    stDates = Array.from({ length: 12 }, (_, i) => {
      const m = i + 2; // Jan tax filed Feb 20, Feb tax → Mar 20, etc.
      const yy = m > 12 ? year + 1 : year;
      const mm = ((m - 1) % 12) + 1;
      return `${yy}-${String(mm).padStart(2, "0")}-20`;
    });
  } else if (annualSalesTax >= 400) {
    stFreq = "Quarterly";
    stDates = [
      `${year}-04-20`, `${year}-07-20`, `${year}-10-20`, `${year + 1}-01-20`,
    ];
  }
  items.push({
    authority: "Pennsylvania DOR",
    name: "PA Sales Tax (6%)",
    form: "PA-3",
    frequency: stFreq,
    dates: stDates,
    portal: "mypath.pa.gov",
    notes: `Frequency auto-selected from $${annualSalesTax.toFixed(0)} YTD collected.`,
  });

  // PA Personal Income Tax (3.07%) — quarterly estimates if you owe > $246/yr
  items.push({
    authority: "Pennsylvania DOR",
    name: "PA Personal Income Tax (3.07%)",
    form: "PA-40ES",
    frequency: "Quarterly estimates",
    dates: [
      `${year}-04-15`, `${year}-06-15`, `${year}-09-15`, `${year + 1}-01-15`,
    ],
    portal: "mypath.pa.gov",
    notes: "Required if you'll owe > $246 in PA tax for the year.",
  });

  // Federal — quarterly + annual
  items.push({
    authority: "IRS (Federal)",
    name: "Estimated Federal Income Tax",
    form: "1040-ES",
    frequency: "Quarterly estimates",
    dates: [
      `${year}-04-15`, `${year}-06-17`, `${year}-09-16`, `${year + 1}-01-15`,
    ],
    portal: "irs.gov/payments",
    notes: "Required if you'll owe ≥ $1,000 not covered by withholding.",
  });
  items.push({
    authority: "IRS (Federal)",
    name: "Annual Income Tax Return",
    form: "1040 + Schedule C",
    frequency: "Annual",
    dates: [`${year + 1}-04-15`],
    portal: "irs.gov",
    notes: "Schedule C reports your Link Ride business income + write-offs.",
  });
  items.push({
    authority: "IRS (Federal)",
    name: "1099-NEC for each driver paid ≥ $600",
    form: "1099-NEC",
    frequency: "Annual",
    dates: [`${year + 1}-01-31`],
    portal: "irs.gov/forms-instructions",
    notes: "Issue to driver AND file with IRS by Jan 31.",
  });

  // Local — West Reading Borough business privilege + Wyomissing SD mercantile
  items.push({
    authority: "West Reading Borough",
    name: "Local Business Privilege Tax",
    form: "Borough mercantile return",
    frequency: "Annual",
    dates: [`${year + 1}-04-15`],
    portal: "westreadingborough.com (or via Berks EIT)",
    notes: "Filed with the borough — separate from the school district mercantile.",
  });
  items.push({
    authority: "Wyomissing Area School District / Berks EIT Bureau",
    name: "Mercantile Tax (Act 511)",
    form: "Mercantile return",
    frequency: "Annual",
    dates: [`${year + 1}-04-15`],
    portal: "berkseit.com",
    notes: "School-district mercantile — applies even though you reside in West Reading.",
  });
  items.push({
    authority: "Berks EIT Bureau",
    name: "Local Earned Income Tax (EIT)",
    form: "Annual EIT return",
    frequency: "Annual",
    dates: [`${year + 1}-04-15`],
    portal: "berkseit.com",
    notes: "Combined municipal + school-district EIT for West Reading residents.",
  });

  return items;
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00").getTime();
  const now = Date.now();
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
}

export default function TaxSummaryPrint() {
  const [params] = useSearchParams();
  const year = parseInt(params.get("year") || String(new Date().getFullYear()), 10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/tax-report`, { params: { year } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year]);

  const schedule = useMemo(() => {
    const salesTax = data?.yearly?.sales_tax || 0;
    return buildSchedule(year, salesTax);
  }, [data, year]);

  if (loading) return <div style={{ padding: 40, fontFamily: "Inter, system-ui" }}>Loading…</div>;
  if (!data) return <div style={{ padding: 40, fontFamily: "Inter, system-ui" }}>Could not load report.</div>;

  const y = data.yearly || {};
  const subs = data.subscriptions?.yearly || { gross: 0, count: 0 };
  const deps = data.deposits?.yearly || { gross: 0, count: 0 };
  const wo = data.writeoffs?.yearly || { total: 0, signup_bonus: 0, admin_pro_comp: 0, admin_credit_grant: 0 };

  const totalRevenue = (y.gross || 0) + subs.gross + deps.gross;
  const netIncome = totalRevenue - (y.driver || 0) - wo.total;

  return (
    <div className="tax-print-root" data-testid="tax-print-root" style={{
      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
      color: "#111",
      background: "#fff",
      padding: "40px 48px",
      maxWidth: "850px",
      margin: "0 auto",
      lineHeight: 1.45,
    }}>
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          .no-print { display: none !important; }
          .tax-print-root { padding: 0 !important; }
          .page-break { page-break-before: always; }
        }
        .h1 { font-size: 28px; font-weight: 800; margin: 0 0 4px; letter-spacing: -0.5px; }
        .h2 { font-size: 16px; font-weight: 700; margin: 28px 0 10px; padding-bottom: 6px;
              border-bottom: 2px solid #111; letter-spacing: 0.5px; text-transform: uppercase; }
        .meta { color: #666; font-size: 12px; margin-bottom: 28px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th { text-align: left; padding: 6px 8px; background: #f3f3f3;
             font-weight: 700; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
        td { padding: 6px 8px; border-bottom: 1px solid #eee; vertical-align: top; }
        td.r, th.r { text-align: right; font-variant-numeric: tabular-nums; }
        .pill { display: inline-block; padding: 1px 8px; border-radius: 99px;
                font-size: 10px; font-weight: 700; }
        .pill-urgent { background: #ffe3e3; color: #c92a2a; }
        .pill-soon   { background: #fff3bf; color: #8b6500; }
        .pill-ok     { background: #e6fcf5; color: #0b7285; }
        .footnote { color: #666; font-size: 10px; margin-top: 8px; font-style: italic; }
      `}</style>

      <div className="no-print" style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <a href="/driver/admin" style={{ fontSize: 12, color: "#0047FF", textDecoration: "none" }}>← Back to Admin</a>
        <button
          onClick={() => window.print()}
          data-testid="print-button"
          style={{
            background: "#0047FF", color: "white", border: 0, padding: "8px 16px",
            borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 13,
          }}>Print / Save as PDF</button>
      </div>

      <div className="h1">Link Ride · Year-End Tax Summary</div>
      <div className="meta">
        Tax Year <strong>{year}</strong> · Prepared {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })} ·
        Owner residence: West Reading Borough, PA · School District: Wyomissing Area
      </div>

      {/* Revenue */}
      <div className="h2">Gross Revenue</div>
      <table>
        <thead>
          <tr><th>Stream</th><th className="r">Count</th><th className="r">Gross</th><th className="r">Sales Tax Collected</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Rides &amp; deliveries (completed)</td>
            <td className="r">{y.count || 0}</td>
            <td className="r">${(y.gross || 0).toFixed(2)}</td>
            <td className="r">${(y.sales_tax || 0).toFixed(2)}</td>
          </tr>
          <tr>
            <td>Link Pro subscriptions</td>
            <td className="r">{subs.count}</td>
            <td className="r">${subs.gross.toFixed(2)}</td>
            <td className="r">—</td>
          </tr>
          <tr>
            <td>Wallet credit-pack deposits</td>
            <td className="r">{deps.count}</td>
            <td className="r">${deps.gross.toFixed(2)}</td>
            <td className="r">—</td>
          </tr>
          <tr style={{ background: "#f9f9f9", fontWeight: 700 }}>
            <td>TOTAL</td>
            <td className="r">{(y.count || 0) + subs.count + deps.count}</td>
            <td className="r">${totalRevenue.toFixed(2)}</td>
            <td className="r">${(y.sales_tax || 0).toFixed(2)}</td>
          </tr>
        </tbody>
      </table>
      <div className="footnote">
        Subscriptions: PA generally does not impose sales tax on digital services — confirm with CPA before remitting.
        Wallet deposits are recognized as income at deposit time (cash-basis); the corresponding booking sales tax is collected separately on redemption.
      </div>

      {/* Payment method breakdown — shows where booking revenue is collected */}
      {data.by_payment_method && (
        <>
          <div className="h2">Booking Revenue by Payment Method</div>
          <table>
            <thead>
              <tr><th>Method</th><th className="r">Trips</th><th className="r">Gross</th></tr>
            </thead>
            <tbody>
              {[
                { key: 'stripe',  label: 'Stripe (online card)' },
                { key: 'jim',     label: 'JIM (tap-to-pay in person)' },
                { key: 'cash',    label: 'Cash' },
                { key: 'credits', label: 'Wallet credits' },
                { key: 'other',   label: 'Other / Unspecified' },
              ].map((m) => {
                const b = data.by_payment_method[m.key] || { gross: 0, count: 0 };
                return (
                  <tr key={m.key}>
                    <td>{m.label}</td>
                    <td className="r">{b.count}</td>
                    <td className="r">${b.gross.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* Expenses & Driver Payouts */}
      <div className="h2">Expenses &amp; Write-offs</div>
      <table>
        <thead>
          <tr><th>Category</th><th className="r">Amount</th></tr>
        </thead>
        <tbody>
          <tr><td>Driver payouts (1099 contractors)</td><td className="r">${(y.driver || 0).toFixed(2)}</td></tr>
          <tr><td>Driver tips passed through</td><td className="r">${(y.tips || 0).toFixed(2)}</td></tr>
          <tr><td>Marketing — signup bonuses</td><td className="r">${wo.signup_bonus.toFixed(2)}</td></tr>
          <tr><td>Marketing — Link Pro comps (fair-market)</td><td className="r">${wo.admin_pro_comp.toFixed(2)}</td></tr>
          <tr><td>Marketing — manual credit grants</td><td className="r">${wo.admin_credit_grant.toFixed(2)}</td></tr>
          <tr style={{ background: "#f9f9f9", fontWeight: 700 }}>
            <td>TOTAL WRITE-OFFS</td>
            <td className="r">${((y.driver || 0) + (y.tips || 0) + wo.total).toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      <div className="h2">Net Income Estimate</div>
      <table>
        <tbody>
          <tr>
            <td>Gross revenue (rides + subscriptions + deposits)</td>
            <td className="r">${totalRevenue.toFixed(2)}</td>
          </tr>
          <tr>
            <td>– Driver payouts</td>
            <td className="r">−${(y.driver || 0).toFixed(2)}</td>
          </tr>
          <tr>
            <td>– Marketing write-offs</td>
            <td className="r">−${wo.total.toFixed(2)}</td>
          </tr>
          <tr style={{ background: "#fff3bf", fontWeight: 700 }}>
            <td>Approximate net income (before federal/state income tax)</td>
            <td className="r">${netIncome.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>
      <div className="footnote">
        Net income is an estimate. Does NOT include Stripe processing fees, vehicle expenses, mileage deductions,
        or other operating costs — your CPA will layer those in.
      </div>

      {/* Mercantile preview */}
      {data.mercantile && (
        <>
          <div className="h2">Wyomissing Area Mercantile Tax Preview</div>
          <table>
            <tbody>
              <tr><td>Taxable volume {year} (gross bookings)</td><td className="r">${data.mercantile.taxable_volume.toFixed(2)}</td></tr>
              <tr><td>Mercantile rate</td><td className="r">{data.mercantile.rate_pct}%</td></tr>
              <tr style={{ background: "#fff3bf", fontWeight: 700 }}>
                <td>Estimated tax due</td><td className="r">${data.mercantile.tax_due.toFixed(2)}</td>
              </tr>
              <tr><td>Filing authority</td><td className="r" style={{ textAlign: "left" }}>{data.mercantile.filing_authority}</td></tr>
              <tr><td>Filing deadline</td><td className="r" style={{ textAlign: "left" }}>{data.mercantile.deadline}</td></tr>
            </tbody>
          </table>
        </>
      )}

      {/* Payment Schedule */}
      <div className="page-break" />
      <div className="h2">Payment Schedule — {year}</div>
      <table data-testid="payment-schedule">
        <thead>
          <tr>
            <th>Authority / Form</th>
            <th>Frequency</th>
            <th>Due Dates</th>
          </tr>
        </thead>
        <tbody>
          {schedule.map((row, i) => (
            <tr key={i}>
              <td>
                <strong>{row.name}</strong><br />
                <span style={{ color: "#666", fontSize: 11 }}>{row.authority} · {row.form}</span><br />
                <span style={{ color: "#888", fontSize: 10 }}>Portal: {row.portal}</span>
                {row.notes && <div style={{ color: "#666", fontSize: 10, marginTop: 4, fontStyle: "italic" }}>{row.notes}</div>}
              </td>
              <td style={{ whiteSpace: "nowrap" }}>{row.frequency}</td>
              <td>
                {row.dates.map((d, di) => {
                  const days = daysUntil(d);
                  let pill = "pill-ok";
                  if (days !== null && days >= 0) {
                    if (days <= 14) pill = "pill-urgent";
                    else if (days <= 45) pill = "pill-soon";
                  }
                  return (
                    <div key={di} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "2px 0" }}>
                      <span>{fmtDate(d)}</span>
                      {days !== null && days >= 0 && (
                        <span className={`pill ${pill}`}>{days === 0 ? "today" : `${days}d`}</span>
                      )}
                    </div>
                  );
                })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="footnote">
        Federal quarterly estimate deadlines shift to the next business day when the 15th falls on a weekend.
        Confirm exact due dates with the IRS / PA DOR each year. This schedule is informational only — your CPA is the final word.
      </div>

      <div style={{ marginTop: 40, padding: 16, background: "#f3f3f3", borderRadius: 8, fontSize: 11, color: "#555" }}>
        <strong>Disclaimer:</strong> This summary is generated from Link Ride's transaction records and is intended to
        speed up your CPA's reconciliation work, not replace it. Tax law is jurisdiction-specific and changes annually.
        Always validate with a licensed tax professional before filing.
      </div>
    </div>
  );
}
